import React, { useState } from 'react';
import {
  Breadcrumbs,
  Link,
  Typography,
  Card,
  CardHeader,
  Box,
  Divider,
  Grid,
  Tab,
  Tabs,
  Avatar,
  Button,
  Backdrop,
} from '@material-ui/core';
import { Link as RouterLink, useHistory, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import mime from 'mime-types';
import FindInPageOutlinedIcon from '@material-ui/icons/FindInPageOutlined';
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined';
import FilterListIcon from '@material-ui/icons/FilterList';
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks';
import CloseIcon from '@material-ui/icons/Close';
import SendOutlinedIcon from '@material-ui/icons/SendOutlined';
import {
  FilterChip,
  Spinner,
  QuickSearch,
  useAPI,
  dateFormatter,
  useAPIAsync,
  VerticalBoxContainer,
  PageContentWrapper,
  ConfirmDialog,
  attributeFieldValues,
  UnsavedChangesDialog,
} from '@shared/components';
import PageHeader from '../../components/PageHeader';
import axios from '../../utils/axios';
import useAuthContext from '../../hooks/useAuthContext';
import useTheme from '../../hooks/useTheme';
import { handleChipDelete } from '../../utils/filterHelpers';
import ExcelExportButton from '../../components/ExcelExportButton';
import useGlobalAlertContext from '../../hooks/useGlobalAlertContext';
import { handleSortChange, getSortDirection } from '../../utils/sortHelpers';
import Table from './Table';
import Filter from './Filter';
import Modal from './Modal';

import AttributeListModal from './TableModal';
import StatusModal from '../Documents/StatusModal';

const fields = [
  {
    label: 'ID',
    value: 'id',
  },
  {
    label: 'Atribut (Metapodatak)',
    value: 'attributeName',
  },
  {
    label: 'Vrijednost',
    value: 'valueDisplayed',
  },
  {
    label: 'Datum kreiranja',
    value: 'dateCreated',
    type: 'dateTime',
  },
  {
    label: 'Korisnik kreirao',
    value: 'createdBy',
  },
];

const getLabelText = (value) => {
  const type = {
    id: 'ID',
    attributeName: 'Atribut (Metpodatak)',
    valueDisplayed: 'Vrijednost',
    dateCreated: 'Datum kreiranja',
    createdBy: 'Korisnik kreirao',
  };
  return type[value];
};

const getChipValue = (chip, filters) => {
  if (chip === 'dateCreated') return dateFormatter(filters[chip]);
  return filters[chip];
};

const DocumentDetails = () => {
  const history = useHistory();
  const theme = useTheme();
  const { id } = useParams();
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({});
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [triggerRerender, setRerender] = useState(false);
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const { user } = useAuthContext();
  // const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [, setDocumentModalVisible] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [sorters, setSorters] = useState([]);
  const {
    data: selectedDocument,
    setData: setDocument,
    loading: apiLoading,
  } = useAPIAsync(axios, `documents/${id}`);

  const [selectedAttribute, setSelectedAttribute] = useState();
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  // const [selectedAttribute, setSelectedAttribute] = useState();
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [journalModalVisible, setJournalModalVisible] = useState(false);

  const { sendSuccessAlertData, sendErrorAlertData } = useGlobalAlertContext();

  const getDocumentFile = async (documentId, extension) => {
    setDownloadLoading(true);
    try {
      const { data } = await axios.get(`get-document/${documentId}`);
      const { documentFile } = data;
      const url = window.URL.createObjectURL(
        new Blob([new Uint8Array(documentFile.data).buffer], {
          type: mime.lookup(extension) || 'application/pdf',
        }),
      );

      const link = document.createElement('a');
      link.href = url;

      link.setAttribute('target', '_blank');

      link.click();
    } catch (documentError) {
      sendErrorAlertData(
        `${documentError?.response?.data?.details?.reason ||
        documentError?.response?.data?.error?.message ||
        `Došlo je do greške prilikom preuzimanja dokumenta`
        }`,
      );
    } finally {
      setDownloadLoading(false);
    }
  };

  const [attributes = { nodes: [], totalCount: 0 }, error, loading] = useAPI(
    () =>
      !apiLoading
        ? axios
          .get(`document-attributes`, {
            params: {
              limit: rowsPerPage,
              offset: page * rowsPerPage,
              conditions: {
                ...filters,
                document: id,
                ...(selectedGroup !== 'all' && { attributeGroupId: selectedGroup }),
              },
              orderBy: [...sorters, '"ordNumber" asc'],
            },
          })
          .then(({ data }) => data)
        : Promise.resolve(() => { }),
    [page, rowsPerPage, filters, sorters, triggerRerender, selectedGroup, apiLoading],
  );

  const [attributeGroups = { nodes: [] }] = useAPI(
    () =>
      !apiLoading
        ? axios.get(`attribute-groups/${id}`).then(({ data }) => data)
        : Promise.resolve(() => { }),
    [triggerRerender, apiLoading],
  );

  const getRandomColorById = (colorId) => {
    const colors = theme.palette.chart_colors;
    return colors[colorId % colors.length];
  };

  // attributes table update
  const methods = useForm();

  const onSubmit = async (values) => {
    const outputArray = Object.entries(values).map(([key, value]) => {
      const objId = key.split('-')[1];
      const attributeObject = attributes.nodes.find((obj) => obj.id.toString() === objId);
      return {
        id: attributeObject.id,
        attribute: attributeObject.attribute,
        document: attributeObject.document,
        documentType: attributeObject.documentType,
        ...attributeFieldValues({ valueDisplayed: value }, attributeObject),
      };
    });
    const { data } = await axios.put(`document-attributes/update/many`, {
      documentAttribute: [...outputArray],
    });
    if (!data) throw new Error();
    setRerender(!triggerRerender);
    methods.reset(values);
    setDialogVisible(false);
    sendSuccessAlertData(`Uspješno izmijenjenjeni atributi dokumenta`);
  };

  const handleTabelReset = () => {
    const values = attributes.nodes.reduce((acc, attribute) => {
      if (attribute.attributeType === 1) {
        acc[`valueDisplayed-${attribute.id}`] = attribute.valueDisplayed;
      }
      return acc;
    }, {});
    methods.reset(values);
    sendSuccessAlertData(`Izmjene uspješno izbrisane`);
  };

  if (apiLoading)
    return (
      <Backdrop
        open
        style={{
          zIndex: theme.zIndex.drawer - 1,
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Spinner size={50} />
      </Backdrop>
    );

  return (
    <VerticalBoxContainer>
      <Filter
        currentValues={filters}
        visible={filterModalVisible}
        setVisible={setFilterModalVisible}
        setFilters={setFilters}
        setPage={setPage}
        getLabelText={getLabelText}
        getChipValue={getChipValue}
      />
      <Modal
        document={selectedDocument}
        attribute={selectedAttribute}
        setSelectedAttribute={setSelectedAttribute}
        visible={modalVisible}
        setVisible={setModalVisible}
        setRerender={setRerender}
        resetAttribute={() => setSelectedAttribute(null)}
        parentData={selectedDocument}
        sendSuccessAlertData={sendSuccessAlertData}
        sendErrorAlertData={sendErrorAlertData}
        disabled={
          selectedDocument?.status === 4 ||
          selectedDocument?.status === 5 ||
          selectedDocument?.status === 6 ||
          selectedDocument?.status === 7 ||
          selectedDocument?.createdBy !== user?.username
        }
      />
      <UnsavedChangesDialog methods={methods} attributes={attributes.nodes} loading={loading} />
      <ConfirmDialog
        open={dialogVisible}
        setOpen={setDialogVisible}
        title="Izmjena atributa dokumenta"
        message="Da li ste sigurni da želite izmijeniti atribute dokumenta?"
        onSubmit={methods.handleSubmit(onSubmit)}
        loading={loading}
        confirmText="Potvrdi"
        denyText="Odustani"
      />
      <AttributeListModal
        documentId={id}
        visible={journalModalVisible}
        setVisible={setJournalModalVisible}
        title="Journal promjena atributa"
        buttonText="Odustani"
      />
      <StatusModal
        document={selectedDocument}
        setDocument={setDocument}
        sendSuccessAlertData={sendSuccessAlertData}
        sendErrorAlertData={sendErrorAlertData}
        setRerender={setRerender}
        visible={statusModalVisible}
        setVisible={setStatusModalVisible}
        detailScreen
      />
      <PageHeader
        title={`Dokument ${id || ''}`}
        subtitle="Detalji dokumenta"
        sub
        showBackIcon
        extra={
          <>
            <Grid container spacing={4}>
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={loading ? <Spinner size={15} /> : <SendOutlinedIcon />}
                  disabled={
                    loading ||
                    selectedDocument?.status !== 0 ||
                    selectedDocument?.createdBy !== user?.username
                  }
                  onClick={() => setStatusModalVisible(true)}
                >
                  <Box fontSize="small">Promjena statusa</Box>
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={loading ? <Spinner size={15} /> : <FindInPageOutlinedIcon />}
                  disabled={loading}
                  onClick={() => setDocumentModalVisible(true)}
                >
                  <Box fontSize="small">Pregled dokumenta</Box>
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={
                    downloadLoading ? <Spinner size={15} /> : <InsertDriveFileOutlinedIcon />
                  }
                  disabled={downloadLoading}
                  onClick={() => {
                    getDocumentFile(id, selectedDocument.documentExtension);
                  }}
                >
                  <Box fontSize="small">Prikaži dokument</Box>
                </Button>
              </Grid>
            </Grid>
          </>
        }
        history={history}
      />
      <PageContentWrapper>
        <Breadcrumbs style={{ paddingBottom: `${theme.spacing(5)}px` }}>
          <Link variant="body1" color="inherit" to="/" component={RouterLink}>
            Početna
          </Link>
          <Link variant="body1" color="inherit" to="/documents" component={RouterLink}>
            Dokumenti
          </Link>
          <Typography variant="body1" color="textPrimary">
            Atributi (metapodaci) za dati dokument
          </Typography>
        </Breadcrumbs>
        <Box
          width="100%"
          bgcolor="background.paper"
          borderRadius="2px 2px 0"
          border={`1px solid ${theme.palette.divider}`}
          borderBottom={0}
          m="-1px"
        >
          <Tabs
            value={selectedGroup}
            onChange={(e, value) => setSelectedGroup(value)}
            indicatorColor="primary"
            aria-label="attribute group tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              style={{
                minWidth: '0',
              }}
              value="all"
              label={
                <Box
                  display="flex"
                  alignItems="center"
                  flexDirection="row"
                  justifyContent="center"
                  gridGap={5}
                >
                  <Avatar
                    variant="rounded"
                    style={{
                      backgroundColor: getRandomColorById('all'),
                      width: '1.5rem',
                      height: '1.5rem',
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                    }}
                  >
                    {attributeGroups.nodes.reduce(
                      (acc, group) => acc + Number(group.attributeCount),
                      0,
                    )}
                  </Avatar>
                  Svi atributi
                </Box>
              }
            />
            {attributeGroups.nodes.map((group) => (
              <Tab
                key={group.id}
                value={group.id}
                style={
                  attributeGroups.nodes.length < 8
                    ? {
                      minWidth: '0',
                    }
                    : {}
                }
                label={
                  <Box
                    display="flex"
                    alignItems="center"
                    flexDirection="row"
                    justifyContent="center"
                    gridGap={5}
                  >
                    <Avatar
                      variant="rounded"
                      style={{
                        backgroundColor: getRandomColorById(group.id),
                        width: '1.5rem',
                        height: '1.5rem',
                        fontSize: '0.75rem',
                      }}
                    >
                      {group.attributeCount || 0}
                    </Avatar>
                    {group.name}
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>
        <Card
          style={{
            borderTopLeftRadius: '0',
            borderTopRightRadius: '0',
          }}
        >
          <CardHeader
            title={
              <QuickSearch
                name="attributeName"
                placeholder="Brza pretraga po nazivu atributa"
                setFilters={setFilters}
                mainFilter={filters}
                setPage={setPage}
              />
            }
            action={
              <Grid container spacing={4}>
                <Grid item>
                  <Button
                    color="primary"
                    disabled={Object.keys(filters).filter((k) => filters[k])?.length === 0}
                    startIcon={<CloseIcon fontSize="small" />}
                    onClick={() => setFilters({})}
                  >
                    <Box fontSize="small">Ukloni sve filtere</Box>
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<FilterListIcon fontSize="small" />}
                    onClick={() => setFilterModalVisible(true)}
                  >
                    <Box fontSize="small">Filteri</Box>
                  </Button>
                </Grid>
                <Grid item>
                  <ExcelExportButton
                    totalCount={attributes.totalCount}
                    fields={fields}
                    sorters={[...sorters, '"ordNumber" asc']}
                    filters={{ ...filters, document: id }}
                    routeName="document-attributes"
                    variant="outlined"
                    fileName={`atributi-dokumenta-${id}`}
                  />
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<LibraryBooksIcon fontSize="small" />}
                    onClick={() => setJournalModalVisible(true)}
                  >
                    <Box fontSize="small">Journal promjena</Box>
                  </Button>
                </Grid>
              </Grid>
            }
          />
          <Divider />
          {Object.keys(filters).filter((k) => filters[k])?.length !== 0 && (
            <>
              <Box ml={2} p={2} display="flex" alignItems="center" flexWrap="wrap">
                {Object.keys(filters).map(
                  (chip) =>
                    filters[chip] !== undefined && (
                      <FilterChip
                        color="primary"
                        variant="outlined"
                        key={chip}
                        label={`${getLabelText(chip)}: ${getChipValue(chip, filters)}`}
                        onDelete={() => handleChipDelete(chip, setFilters, setPage)}
                      />
                    ),
                )}
              </Box>
            </>
          )}
          <Table
            loading={loading || apiLoading}
            error={error}
            attributes={attributes.nodes}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            totalCount={attributes.totalCount}
            handleSortChange={handleSortChange}
            getSortDirection={getSortDirection}
            setSorters={setSorters}
            sorters={sorters}
            setSelectedAttribute={setSelectedAttribute}
            setEditModalVisible={setModalVisible}
            sendSuccessAlertData={sendSuccessAlertData}
            // update
            triggerRerender={triggerRerender}
            methods={methods}
            onSubmit={onSubmit}
            setDialogVisible={setDialogVisible}
            handleTabelReset={handleTabelReset}
            setRerender={setRerender}
            disabledEdit={
              selectedDocument?.status === 4 ||
              selectedDocument?.status === 5 ||
              selectedDocument?.status === 6 ||
              selectedDocument?.status === 7 ||
              selectedDocument?.createdBy !== user?.username
            }
          />
        </Card>
      </PageContentWrapper>
    </VerticalBoxContainer>
  );
};

export default DocumentDetails;
